import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { LanguageCombobox } from "@/components/LanguageCombobox";
import { isSignedLanguage } from "@/lib/signedLanguages";
import { nativeLanguageLabel, useNativeLanguageOptions } from "@/lib/languages";

interface NativeLanguageSelectProps {
  value: string | null;
  onChange: (id: string) => void;
  id?: string;
  className?: string;
  disabled?: boolean;
  // Language ids to keep off the list. UpdateStudentNativeLanguage refuses a
  // language the student is LEARNING, so the prompt that asks a stuck student
  // (#878) hides those rather than letting them pick a certain refusal.
  exclude?: string[];
}

// The one native-language picker (onboarding step 1, Settings → Languages and
// the flashcards L1 prompt). Options come from Query.nativeLanguageOptions —
// every non-signed Language row — labelled by endonym ("Français", "日本語")
// so a student finds their mother tongue under its own name whatever the UI
// language, searchable by that name, the English name or an alias.
export default function NativeLanguageSelect({ value, onChange, id, className, disabled, exclude }: NativeLanguageSelectProps) {
  const { t } = useTranslation();
  const { data: options = [], isLoading } = useNativeLanguageOptions();
  const spoken = useMemo(() => options.filter((l) => !isSignedLanguage(l.code)), [options]);

  return (
    <LanguageCombobox
      id={id}
      value={value}
      onChange={onChange}
      options={spoken}
      exclude={exclude}
      labelFor={nativeLanguageLabel}
      placeholder={t("studentOnboarding.nativeLanguage.placeholder")}
      disabled={disabled || isLoading}
      triggerClassName={className}
    />
  );
}
